import { defineMessages } from 'react-intl';
import groupBy from 'lodash/groupBy';

import { fg } from '@confluence/feature-gating';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { isDeprecatedConnectWebItem } from '@confluence/web-item/entry-points/isDeprecatedConnectWebItem.ts';

import type { WebItem, MappedWebItem } from './mapWebItem.tsx';
import { mapWebItem } from './mapWebItem.tsx';

// eslint-disable-next-line check-i18n-id-prefix/i18n-id-prefix-with-package-name-defineMessages
const i18n = defineMessages({
	'system.user/adg3-section': {
		id: 'navigation.help.us.improve',
		defaultMessage: 'Help us improve',
		description: 'Section heading for user feedback and improvement in navigation',
	},
	'system.user/user-content': {
		id: 'navigation.adg3.user.profile.user-content',
		defaultMessage: 'Confluence',
		description: 'Label for Confluence in the user profile navigation section',
	},
	'system.help/pages': {
		id: 'navigation.help.name',
		defaultMessage: 'Help',
		description: 'Label for the Help section in the navigation menu',
	},
	'system.help/pages/legal-section': {
		id: 'navigation.system.help.legal',
		defaultMessage: 'Legal',
		description: 'Label for the Legal section under Help in the navigation',
	},
});

type GroupedWebItems = {
	[key: string]: WebItem[];
};

export type FilteredItems = {
	heading:
		| string
		| {
				id: string;
				defaultMessage: string;
		  }
		| null;
	items: Array<Partial<MappedWebItem>>;
};

type Items = Array<FilteredItems>;

interface WebItemMapperOptions {
	userId?: string;
	callbackActions?: {
		[moduleKey: string]: () => void;
	};
}

export const useWebItemMapper = (
	webItems: WebItem[],
	webSections: (string | null)[],
	options: WebItemMapperOptions,
): Items => {
	const { push } = useRouteActions();
	const items: Items = [];

	if (webItems && webItems.length) {
		const groupedSections: GroupedWebItems = groupBy(webItems, 'section');
		webSections.forEach((sectionName) => {
			if (!sectionName) {
				return;
			}

			const webItemRecords = groupedSections[sectionName];

			if (!webItemRecords) {
				return;
			}

			const tempItems = webItemRecords
				.map((webItemRecord) => {
					// MYCL-33: deprecate the Confluence↔Slack "Slack" user settings item (the Slack
					// Connect app's `profile` general-page). The id check is first so the gate is only
					// exposed when the item is present.
					if (
						isDeprecatedConnectWebItem(webItemRecord.id) &&
						// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
						fg('twcg-2215-confluence-slack-deprecate-user-settings')
					) {
						return undefined;
					}
					return mapWebItem(
						webItemRecord,
						push,
						options?.userId,
						webItemRecord.moduleKey && options?.callbackActions
							? options?.callbackActions[webItemRecord.moduleKey]
							: undefined,
						// isTaskHomeEnabled
					);
				})
				.filter((item): item is Partial<MappedWebItem> => !!item);

			items.push({
				heading: i18n.hasOwnProperty(sectionName) ? { ...i18n[sectionName] } : null,
				items: tempItems,
			});
		});
	}

	return items;
};
